/**
 * Email message formatting (2026-09-28). The optional message typed when a
 * proposal is sent supports a SMALL, safe subset - enough for a bolded line
 * and a list, not a document editor:
 *
 *   **bold**   *italic*   [text](https://link)
 *   - bullet / * bullet        1. numbered
 *   blank line = new paragraph; single line break kept inside a paragraph.
 *
 * And three PLACEMENT tokens for the proposal's own pieces:
 *   {button}    the "View your proposal" button (on its own line)
 *   {link}      the plain proposal link
 *   {password}  the link's password
 * Without {button}/{link} the default layout is used: button above the
 * message, plain link below. The password is always included when it is
 * meant to be (added at the bottom if {password} is not placed).
 *
 * Everything is HTML-escaped FIRST, then the markers are turned into tags,
 * so nothing the operator types can inject markup. Links must be http(s)
 * or mailto. The same code runs in the backend mailer
 * (js_crm_backend src/proposal/template/message-format.ts) - KEEP THE TWO IN STEP.
 */

const esc = (s: string) =>
  String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

export interface MessageTokens {
  /** Block HTML for a line that is exactly {button}. */
  button?: string;
  /** Inline HTML for {button} inside a sentence. */
  buttonInline?: string;
  link?: string;
  password?: string;
}

const inline = (s: string, t: MessageTokens = {}) =>
  esc(s)
    .replace(/\[([^\]]+)\]\(((?:https?:\/\/|mailto:)[^\s)]+)\)/g, '<a href="$2" style="color:#0b5394">$1</a>')
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/(^|[^*])\*([^*\n]+)\*(?!\*)/g, '$1<em>$2</em>')
    .replace(/\{link\}/gi, t.link ?? '{link}')
    .replace(/\{password\}/gi, t.password ?? '{password}')
    .replace(/\{button\}/gi, t.buttonInline ?? t.link ?? '{button}');

const P = 'margin:0 0 12px;font-size:14px;line-height:1.6';
const LIST = 'margin:0 0 12px;padding-left:22px;font-size:14px;line-height:1.6';

/** The message as email-safe HTML. Tokens render only when `tokens` supplies them. */
export function messageToHtml(message: string | null | undefined, tokens: MessageTokens = {}): string {
  const lines = String(message ?? '').replace(/\r\n?/g, '\n').split('\n');
  const out: string[] = [];
  let para: string[] = [];
  let list: { tag: 'ul' | 'ol'; items: string[] } | null = null;

  const flushPara = () => {
    if (para.length) out.push(`<p style="${P}">${para.map((l) => inline(l, tokens)).join('<br>')}</p>`);
    para = [];
  };
  const flushList = () => {
    if (list) out.push(`<${list.tag} style="${LIST}">${list.items.map((i) => `<li>${inline(i, tokens)}</li>`).join('')}</${list.tag}>`);
    list = null;
  };

  for (const raw of lines) {
    const line = raw.trimEnd();
    if (/^\s*\{button\}\s*$/i.test(line) && tokens.button) {
      flushPara();
      flushList();
      out.push(tokens.button);
      continue;
    }
    const bullet = /^\s*[-*\u2022]\s+(.*)$/.exec(line);
    const num = /^\s*\d+[.)]\s+(.*)$/.exec(line);
    const item = bullet ?? num;
    if (item) {
      flushPara();
      const tag = bullet ? 'ul' : 'ol';
      if (!list || list.tag !== tag) { flushList(); list = { tag, items: [] }; }
      list.items.push(item[1]);
      continue;
    }
    if (!line.trim()) { flushPara(); flushList(); continue; }
    flushList();
    para.push(line);
  }
  flushPara();
  flushList();
  return out.join('\n');
}

/**
 * The whole proposal email body - the one place its layout lives, used by
 * the mailer and (mirrored) by the composer's Preview.
 */
export function buildProposalEmailHtml(input: {
  message: string | null | undefined;
  shareUrl: string;
  password?: string | null;
  includePassword: boolean;
  fromName: string;
  replyTo?: string | null;
}): string {
  const msg = String(input.message ?? '');
  const has = (t: string) => new RegExp(`\\{${t}\\}`, 'i').test(msg);
  const url = esc(input.shareUrl);
  const button = `<p style="margin:0 0 20px">
  <a href="${url}"
     style="display:inline-block;background:#1a2332;color:#ffffff;text-decoration:none;
            padding:13px 28px;border-radius:8px;font-weight:700;font-size:14px">
    View your proposal
  </a>
</p>`;
  const link = `<a href="${url}" style="color:#0b5394;word-break:break-all">${url}</a>`;
  const showPassword = !!(input.includePassword && input.password);
  const password = showPassword ? `<b>${esc(input.password as string)}</b>` : '(sent in a separate email)';

  const placed = has('button') || has('link');
  const body = messageToHtml(msg, {
    button,
    buttonInline: `<a href="${url}" style="color:#0b5394;font-weight:700">View your proposal</a>`,
    link,
    password,
  });

  return [
    placed ? '' : button,
    body,
    placed ? '' : `<p style="margin:24px 0 4px;font-size:13px;color:#555">\n  Or open it directly:<br>\n  ${link}\n</p>`,
    showPassword && !has('password') ? `<p style="margin:8px 0 0;font-size:13px;color:#555">Password: ${password}</p>` : '',
    `<p style="margin:20px 0 0;font-size:12px;color:#888">${esc(input.fromName)}${input.replyTo ? ` \u00b7 <a href="mailto:${esc(input.replyTo)}" style="color:#888">${esc(input.replyTo)}</a>` : ''} \u00b7 GetMulch.com</p>`,
  ]
    .filter(Boolean)
    .join('\n');
}
